import {
    Component,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { AppSettingFetcherService } from '@masar/core/services';
import { formatKpiFixedNumber } from '@masar/common/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-achieved-container',
    template: `
        @if (achieved === 0 || achieved) {
            <span
                class="flex h-full w-full items-center justify-center px-3 py-3 text-xs font-semibold"
                [ngStyle]="{ backgroundColor: bgColor, color: color }"
            >
                {{ formatAchieved() }}
            </span>
        }
    `,
    styles: [
        ':host { display: flex; align-items: stretch; justify-content: center; width: 100%; height: 100%; }',
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AchievedContainerComponent implements OnDestroy, OnChanges {
    @Input() public achieved: number;
    @Input() public decimalPlaces: number;

    public color: string;
    public bgColor: string;
    private unsubscribeAll = new Subject<void>();

    public constructor(
        private appSettingFetcherService: AppSettingFetcherService,
        @Inject(LOCALE_ID) private readonly locale: string
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['achieved']) this.refresh();
        if (changes['decimalPlaces']) this.refresh();
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }
    public formatAchieved(): string {
        return `${formatKpiFixedNumber(
            this.achieved * 100,
            this.decimalPlaces,
            {
                trimTrailingZeros: true,
                thousandSeparator: true,
                locale: this.locale,
            }
        )}%`;
    }
    private setColors(hex: string): void {
        this.color = hex;
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            const r = parseInt(result[1], 16);
            const g = parseInt(result[2], 16);
            const b = parseInt(result[3], 16);
            this.bgColor = `rgba(${r}, ${g}, ${b}, 0.15)`;
        } else {
            this.bgColor = hex;
        }
    }

    private refresh(): void {
        this.unsubscribeAll.next();

        this.appSettingFetcherService.get$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(({ kpiSetting }) => {
                for (const coding of kpiSetting.achievementColorCodings) {
                    if (
                        (coding.min === null || coding.min <= this.achieved) &&
                        (coding.max === null || this.achieved < coding.max)
                    ) {
                        this.setColors(coding.color);

                        return;
                    }
                }

                this.setColors('#aaaaaa');
            });
    }
}
